import type { JsonValue } from '@inf-monkeys-tech/monkeys';

export type TypedSecretInputKind = 'password' | 'text' | 'textarea' | 'boolean' | 'number' | 'json';
export type TypedSecretInputResult = { status: 'valid'; value: JsonValue } | { status: 'empty' } | { status: 'invalid'; code: 'invalid-kind' | 'invalid-type' | 'invalid-number' | 'invalid-json' | 'unsafe-json' | 'input-budget-exceeded' };
export const TYPED_SECRET_MAX_BYTES = 1024 * 1024;
/** Parse only transient control input. Callers may put valid values in secretChanges, never the draft. */
export function parseTypedSecretInput(kind: unknown, raw: unknown): TypedSecretInputResult {
  const resolved = kind === undefined ? 'password' : kind;
  if (typeof resolved !== 'string' || !['password', 'text', 'textarea', 'boolean', 'number', 'json'].includes(resolved)) return { status: 'invalid', code: 'invalid-kind' };
  if (raw === undefined) return { status: 'empty' };
  if (raw === '' && resolved !== 'password' && resolved !== 'text' && resolved !== 'textarea') return { status: 'empty' };
  if (resolved === 'boolean') {
    if (raw === true || raw === 'true') return { status: 'valid', value: true };
    if (raw === false || raw === 'false') return { status: 'valid', value: false };
    return { status: 'invalid', code: 'invalid-type' };
  }
  if (typeof raw !== 'string') return { status: 'invalid', code: 'invalid-type' };
  if (raw.length > TYPED_SECRET_MAX_BYTES || new TextEncoder().encode(raw).byteLength > TYPED_SECRET_MAX_BYTES) return { status: 'invalid', code: 'input-budget-exceeded' };
  if (resolved === 'password' || resolved === 'text' || resolved === 'textarea') return { status: 'valid', value: raw };
  if (!raw.trim()) return { status: 'empty' };
  if (resolved === 'number') {
    const text = raw.trim();
    if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i.test(text) || !Number.isFinite(Number(text))) return { status: 'invalid', code: 'invalid-number' };
    return { status: 'valid', value: Number(text) };
  }
  let parsed: unknown;
  try { parsed = JSON.parse(raw); } catch { return { status: 'invalid', code: 'invalid-json' }; }
  let nodes = 0;
  let error: 'unsafe-json' | 'input-budget-exceeded' | undefined;
  const visit = (value: unknown, depth: number): value is JsonValue => {
    if (++nodes > 16384 || depth > 32) { error = 'input-budget-exceeded'; return false; }
    if (typeof value === 'number') { if (!Number.isFinite(value)) { error = 'unsafe-json'; return false; } return true; }
    if (value === null || typeof value === 'string' || typeof value === 'boolean') return true;
    if (typeof value !== 'object') { error = 'unsafe-json'; return false; }
    for (const [key, child] of Object.entries(value)) {
      if (['__proto__', 'constructor', 'prototype'].includes(key)) { error = 'unsafe-json'; return false; }
      if (!visit(child, depth + 1)) return false;
    }
    return true;
  };
  if (!visit(parsed, 0)) return { status: 'invalid', code: error ?? 'unsafe-json' };
  return { status: 'valid', value: parsed };
}
